import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { HeroSection } from '@/components/dashboard/HeroSection';
import { PathwaySelector } from '@/components/dashboard/PathwaySelector';
import { ProgressSection } from '@/components/dashboard/ProgressSection';
import { LaunchSection } from '@/components/dashboard/LaunchSection';
import OnlineCoaching from '@/components/dashboard/OnlineCoaching';
import ProfessionalNetworking from '@/components/dashboard/ProfessionalNetworking';
import OnboardingModal from '@/components/onboarding/OnboardingModal';
import { dashboardService, type SkillPathway, type DashboardData } from '@/services/dashboardService';
import type { Event, EventEnrollment } from '../types/event';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { toast } from 'react-toastify';
// import { Tooltip } from 'react-tooltip';
import './Dashboard.css';

// Timeout configuration (in milliseconds)
const LOADING_TIMEOUT = 15000; // 15 seconds
const AUTH_TIMEOUT = 10000; // 10 seconds for auth initialization
const AUTO_CLOSE_DELAY = 2000; // 2 seconds to auto-close modal after data loads

const Dashboard: React.FC = () => {
  const [userData, setUserData] = useState<DashboardData | null>(null);
  const [coachingData, setCoachingData] = useState<Event[] | []>([]);
  const [networkingData, setNetworkingData] = useState<Event[] | []>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coaLoading, setCoaLoading] = useState(true);
  const [coaError, setCoaError] = useState<string | null>(null);
  const [netLoading, setNetLoading] = useState(true);
  const [netError, setNetError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasSubscription, setHasSubscription] = useState<boolean>(false);
  const [selectedTree, setSelectedTree] = useState<SkillPathway|null>(null);
  const [selectedWay, setSelectedWay] = useState<SkillPathway|null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  
  // New states for timeout handling
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [isLoadingStuck, setIsLoadingStuck] = useState(false);
  const [timeoutMessage, setTimeoutMessage] = useState<string>('');
  const [modalAutoCloseTimer, setModalAutoCloseTimer] = useState<NodeJS.Timeout | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isLoading: isAuthLoading, isAuthenticated, logout } = useUser();
  
  // Refs to track timeouts
  const authTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dataTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const overallTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMountRef = useRef(true);
  const previousAuthStateRef = useRef<boolean | null>(null);

  // Auto-close modal when data loads successfully
  useEffect(() => {
    // Check if modal is open and data has loaded
    if (showTimeoutModal && !loading && !coaLoading && !netLoading && userData) {
      // Set a timer to auto-close the modal after a short delay
      const timer = setTimeout(() => {
        setShowTimeoutModal(false);
        setIsLoadingStuck(false);
      }, AUTO_CLOSE_DELAY);
      
      setModalAutoCloseTimer(timer);
      
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
    
    // Clear auto-close timer if modal is closed or data is loading again
    if (!showTimeoutModal && modalAutoCloseTimer) {
      clearTimeout(modalAutoCloseTimer);
      setModalAutoCloseTimer(null);
    }
  }, [showTimeoutModal, loading, coaLoading, netLoading, userData]);

  // Handle unauthenticated users - redirect to login
  useEffect(() => {
    // Skip if auth is still loading
    if (isAuthLoading) {
      return;
    }
    
    // Check if user became unauthenticated
    if (previousAuthStateRef.current === true && !isAuthenticated && !user) {
      console.warn('User authentication lost, redirecting to login...');
      
      // Show a toast notification
      toast.info('Your session has expired. Please login again.', {
        autoClose: 3000,
        position: 'top-center'
      });
      
      // Redirect to login page after a short delay
      const redirectTimer = setTimeout(() => {
        navigate('/login', { 
          replace: true,
          state: { 
            from: location.pathname,
            reason: 'session_expired'
          }
        });
      }, 1500);
      
      return () => clearTimeout(redirectTimer);
    }
    
    // Update previous auth state
    previousAuthStateRef.current = isAuthenticated;
  }, [isAuthLoading, isAuthenticated, user, navigate, location.pathname]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      setIsLoadingStuck(false);
      
      // Clear any existing timeout
      if (dataTimeoutRef.current) {
        clearTimeout(dataTimeoutRef.current);
      }
      
      // Set timeout for data loading
      dataTimeoutRef.current = setTimeout(() => {
        if (loading) {
          setIsLoadingStuck(true);
          setTimeoutMessage(
            'Your dashboard data is taking longer than expected. ' +
            'This could be due to network issues or server delays.'
          );
          setShowTimeoutModal(true);
        }
      }, LOADING_TIMEOUT);
      
      const [data, profileResponse] = await Promise.all([
        dashboardService.getDashboardData(),
        dashboardService.getUserProfile()
      ]);
      setUserData(data);
      
      // Check subscription status from profile API
      const profileData = (profileResponse?.data as any)?.data as any;
      const subscription = profileData?.subscription || profileData?.data?.subscription;
      
      // Check if subscription exists and is not expired
      let hasActiveSubscription = false;
      if (subscription) {
        // Check if subscription has expire_date and if it's in the future
        if (subscription.expire_date) {
          const expireDate = new Date(subscription.expire_date);
          const now = new Date();
          hasActiveSubscription = expireDate > now;
        } else if (subscription.is_active !== undefined) {
          // Use is_active flag if available
          hasActiveSubscription = subscription.is_active === true;
        } else {
          // Fallback: if subscription exists but no expiration/active info, assume active
          // (This handles cases where API only returns active subscriptions)
          hasActiveSubscription = true;
        }
      }
      
      setHasSubscription(hasActiveSubscription);
      
      // Clear timeout on success
      if (dataTimeoutRef.current) {
        clearTimeout(dataTimeoutRef.current);
        dataTimeoutRef.current = null;
      }
      
      // If modal is open and data loaded, show success message
      if (showTimeoutModal) {
        toast.success('Dashboard data loaded successfully!', {
          autoClose: 3000,
          position: 'top-center'
        });
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      setError('Failed to load dashboard data');
      setHasSubscription(false);
      
      // Set specific error message for timeout modal
      if (error instanceof Error && error.message.includes('timeout')) {
        setTimeoutMessage(
          'The request timed out. This could be due to network issues. ' +
          'Please check your internet connection and try again.'
        );
        setShowTimeoutModal(true);
      } else if (error instanceof Error && (error.message.includes('401') || error.message.includes('unauthorized'))) {
        // Handle unauthorized errors
        setTimeoutMessage(
          'Your session appears to have expired. ' +
          'Please login again to continue.'
        );
        setShowTimeoutModal(true);
      } else {
        setTimeoutMessage(
          'We encountered an error while loading your data. ' +
          'This could be a temporary issue with our servers.'
        );
        setShowTimeoutModal(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEventsData = async () => {
    try {
      setCoaLoading(true);
      setNetLoading(true);
      setCoaError(null);
      setNetError(null);
      
      const req = await dashboardService.getEvents();
      // console.log('Events data:', req.data);
      // setUserData(data);
      const coaching = (req?.data as any)?.coaching as any || [];
      const networking = (req?.data as any)?.networking as any || [];
      setCoachingData(coaching);
      setNetworkingData(networking);
      
      // If modal is open and events loaded, update loading states
      if (showTimeoutModal && !coaLoading && !netLoading) {
        // Events loaded successfully
      }
    } catch (error) {
      console.error('Failed to load events data:', error);
      setCoaError('Failed to load coaching events');
      setNetError('Failed to load networking events');
      
      // Don't show modal for events errors, just log them
      if (!showTimeoutModal) {
        toast.error('Some events data failed to load. You can try refreshing.', {
          autoClose: 5000
        });
      }
    } finally {
      setCoaLoading(false);
      setNetLoading(false);
    }
  };

  // Handle authentication timeout
  useEffect(() => {
    if (isAuthLoading) {
      authTimeoutRef.current = setTimeout(() => {
        if (isAuthLoading && isInitialMountRef.current) {
          setIsLoadingStuck(true);
          setTimeoutMessage(
            'Authentication is taking longer than expected. ' +
            'This could be due to network issues or session problems.'
          );
          setShowTimeoutModal(true);
        }
      }, AUTH_TIMEOUT);
    } else {
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
    }
    
    return () => {
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
    };
  }, [isAuthLoading]);

  // Wait for authentication to initialize before loading data
  useEffect(() => {
    // Don't load data until auth is initialized
    if (isAuthLoading) {
      return;
    }
    
    // If not authenticated, redirect will be handled by ProtectedRoute or UserContext
    if (!isAuthenticated && !user) {
      // Additional safety check - redirect immediately if not authenticated
      const redirectTimer = setTimeout(() => {
        navigate('/login', { 
          replace: true,
          state: { from: location.pathname }
        });
      }, 100);
      
      return () => clearTimeout(redirectTimer);
    }
    
    // Set overall timeout for initial mount
    if (isInitialMountRef.current) {
      overallTimeoutRef.current = setTimeout(() => {
        if (loading && isInitialMountRef.current) {
          setIsLoadingStuck(true);
          setTimeoutMessage(
            'Dashboard initialization is taking longer than expected. ' +
            'This could be due to server delays or network connectivity issues.'
          );
          setShowTimeoutModal(true);
        }
      }, LOADING_TIMEOUT + 5000);
    }
    
    loadUserData();
    loadEventsData();
    
    // Mark initial mount as complete after first load attempt
    isInitialMountRef.current = false;
    
    return () => {
      if (overallTimeoutRef.current) {
        clearTimeout(overallTimeoutRef.current);
        overallTimeoutRef.current = null;
      }
    };
  }, [isAuthLoading, isAuthenticated, user]);

  // Clean up all timeouts on unmount
  useEffect(() => {
    return () => {
      if (authTimeoutRef.current) clearTimeout(authTimeoutRef.current);
      if (dataTimeoutRef.current) clearTimeout(dataTimeoutRef.current);
      if (overallTimeoutRef.current) clearTimeout(overallTimeoutRef.current);
      if (modalAutoCloseTimer) clearTimeout(modalAutoCloseTimer);
    };
  }, []);

  // Handle payment success redirect
  useEffect(() => {
    // Wait for auth to be ready and authenticated before handling payment success
    if (isAuthLoading || !isAuthenticated) {
      return;
    }

    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      // Show success toast
      toast.success('Your subscription has been processed successfully.');

      // Remove query parameters from URL
      searchParams.delete('payment');
      searchParams.delete('session_id');
      navigate(`/dashboard${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, { replace: true });
      
      // Small delay to ensure auth is fully initialized, then refresh user data
      setTimeout(() => {
        loadUserData();
      }, 500);
    }
  }, [searchParams, navigate, isAuthLoading, isAuthenticated]);

  // Update body class based on layout
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      const originalClasses = mainElement.className;
      mainElement.setAttribute('data-ori-cls', originalClasses);
      
      mainElement?.classList.remove('2xl:w-1/3', 'xl:w-2/5', 'lg:w-1/2', 'md:w-3/5', 'max-w-7xl');      
      mainElement?.classList.add('w-full');
    }

    // Cleanup on unmount
    return () => {
      if (mainElement) {
        // Get the original classes from data attribute
        const originalClasses = mainElement.getAttribute('data-ori-cls');

        // Remove and restore original classes if they exist
        if (originalClasses) {
          // Remove the current classes
          mainElement.className = '';
          mainElement.className = originalClasses;
        }
      }
    };
  }, [location.pathname]);

  useEffect(() => {
    // Show onboarding modal only if user has subscription AND no enrolled pathways (ever)
    // Use total_skillstrees from statistics to check if user has ever enrolled
    const totalEnrolled = userData?.statistics?.total_skillstrees || 0;
    if (userData && hasSubscription && totalEnrolled === 0) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [userData, hasSubscription]);

  // Timeout Modal Handlers
  const handleRetryLoading = async () => {
    setShowTimeoutModal(false);
    setIsLoadingStuck(false);
    
    // Clear auto-close timer
    if (modalAutoCloseTimer) {
      clearTimeout(modalAutoCloseTimer);
      setModalAutoCloseTimer(null);
    }
    
    // Show loading state
    setLoading(true);
    setCoaLoading(true);
    setNetLoading(true);
    
    // Clear any existing data
    setUserData(null);
    setCoachingData([]);
    setNetworkingData([]);
    
    // Retry loading all data
    try {
      await loadUserData();
      await loadEventsData();
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const handleLogoutAndRetry = async () => {
    setShowTimeoutModal(false);
    setIsLoadingStuck(false);
    
    // Clear auto-close timer
    if (modalAutoCloseTimer) {
      clearTimeout(modalAutoCloseTimer);
      setModalAutoCloseTimer(null);
    }
    
    // Call logout function if available
    if (logout) {
      await logout();
    }
    
    // Navigate to login page
    navigate('/login');
  };

  const handleContactSupport = () => {
    window.open('/support', '_blank');
  };

  const handleCloseModal = () => {
    setShowTimeoutModal(false);
    setIsLoadingStuck(false);
    
    // Clear auto-close timer
    if (modalAutoCloseTimer) {
      clearTimeout(modalAutoCloseTimer);
      setModalAutoCloseTimer(null);
    }
  };

  // Show loading while auth is initializing
  // IMPORTANT: This must be AFTER all hooks to maintain hook order
  if (isAuthLoading && !isLoadingStuck) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading dashboard..." />
      </div>
    );
  }

  const handlePathwaySelect = async (pathwayId: string) => {
    // await loadUserData(); // Refresh data after selection
  };

  const handleTreeSelect = async (tree: SkillPathway) => {   
    setTreeLoading(true); // Start loading when tree is selected
    setSelectedTree(tree);
    if(tree?.skillstree_id) {
      try {
        const response = await dashboardService.getSkillstree(tree.skillstree_id.toString());
        let responseData = response.data as any || null;
        if (responseData?.data) {
          responseData = responseData.data || null;
        }
        setSelectedWay(responseData as SkillPathway || null);
      } catch (error) {
        console.error('Failed to load tree data:', error);
      } finally {
        setTreeLoading(false); // End loading regardless of success/failure
      }
    } else {
      setTreeLoading(false);
    }
  };

  const handleProgressUpdate = async (percentage: number) => {
    await loadUserData(); // Refresh data after progress update
  };

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
  };

  const handleOnboardingContinue = async (categoryId: number, skillstreeId: number) => {
    setShowOnboarding(false);
    navigate(`/skillstree/${categoryId}/${skillstreeId}`);
  };

  const hasData = userData && userData?.pathwayCount > 0;

  return (
    <>
      {/* Timeout Modal */}
      {showTimeoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-orange-600 rounded-2xl max-w-md w-full p-6 shadow-2xl relative overflow-y-auto max-h-[90vh]">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-600/20 mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Loading Timeout</h3>
              <p className="text-gray-300 mb-4">
                {timeoutMessage}
              </p>
              
              {/* Loading Status Indicator */}
              <div className="mb-3 p-3">
                <div className="flex items-start justify-start gap-4 mb-2">
                  <div className={`w-3 h-3 rounded-full ${loading ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`}></div>
                  <span className="text-sm text-gray-300">Dashboard Data: {loading ? 'Loading...' : 'Loaded'}</span>
                </div>
                <div className="flex items-start justify-start gap-4 mb-2">
                  <div className={`w-3 h-3 rounded-full ${coaLoading ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`}></div>
                  <span className="text-sm text-gray-300">Online Coaching Events: {coaLoading ? 'Loading...' : 'Loaded'}</span>
                </div>
                <div className="flex items-start justify-start gap-4">
                  <div className={`w-3 h-3 rounded-full ${netLoading ? 'bg-orange-500 animate-pulse' : 'bg-green-500'}`}></div>
                  <span className="text-sm text-gray-300">Professional Networking Events: {netLoading ? 'Loading...' : 'Loaded'}</span>
                </div>
              </div>
              
              <div className="text-sm text-gray-400 bg-gray-900/50 p-3 rounded-lg mb-6">
                <p className="text-left mb-1">Possible reasons:</p>
                <ul className="text-left list-disc pl-5 space-y-1">
                  <li>Slow internet connection</li>
                  <li>Temporary server issues</li>
                  <li>Browser cache problems</li>
                  <li>Session timeout</li>
                </ul>
              </div>
              
              {/* Auto-close countdown */}
              {!loading && !coaLoading && !netLoading && userData && (
                <div className="text-green-400 text-sm mb-4 animate-pulse">
                  ✓ Data loaded! Modal will close automatically...
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <button
                onClick={handleRetryLoading}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry Loading Data
              </button>
              
              <button
                onClick={handleRefreshPage}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Page
              </button>
              
              <button
                onClick={handleLogoutAndRetry}
                className="w-full bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Login Again
              </button>
              
              {/* <button
                onClick={handleContactSupport}
                className="w-full bg-transparent border border-gray-600 hover:border-gray-500 text-gray-300 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Contact Support
              </button> */}
            </div>
            
            <p className="text-xs text-gray-500 text-center mt-6">
              If the issue persists, please contact our support team.
            </p>
          </div>
        </div>
      )}

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto min-h-screen bg-transparent text-white">
        <HeroSection 
          userData={userData as any} 
          selectedTree={selectedTree} 
          treeLoading={loading}
        />
        
        <div className="mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <PathwaySelector 
                pathways={userData?.skillPathways as any} 
                groupedPathways={userData?.groupedPathways as any}
                selectedPathway={userData?.selectedPathway as any}
                onPathwaySelect={handlePathwaySelect}
                onTreeSelect={handleTreeSelect}
                loading={loading}
              />
              
              <a href='/faq' data-tooltip-id="pathway-need-help-tooltip" data-tooltip-content="Need Help!" data-tooltip-position-strategy='fixed' className="hidden lg:block mt-10 flex items-center justify-center lg:items-end lg:justify-start">
                <img src="/pages/dashboard/astronaut-need-help.gif" alt="FAQ Link" className="w-40 h-40 object-contain" />
                {/* <Tooltip id="pathway-need-help-tooltip" isOpen={true} className="!bg-white !text-black !font-bold !rounded-tl-xl !rounded-tr-xl !rounded-br-xl xl:-ml-10 lg:-ml-2 ml-20" classNameArrow="!-left-3.5 !rounded-br-lg cut-corner-bl !overflow-hidden " arrowSize={30} /> */}
              </a>
            </div>
            
            <div className="lg:col-span-2">
              <ProgressSection 
                progress={userData?.progress as any}
                hasData={hasData as any}
                onProgressUpdate={handleProgressUpdate}
                selectedTree={selectedTree}
                selectedWay={selectedWay}
                loading={treeLoading}
              />
              
              <div className="mt-8 flex sm:flex-row flex-col-reverse items-center lg:justify-end justify-center sm:justify-between">
                <a href='/faq' data-tooltip-id="launch-sneed-help-tooltip" data-tooltip-content="Need Help!" data-tooltip-position-strategy='fixed' className="lg:hidden mt-10 flex items-center justify-center lg:items-end lg:justify-start">
                  <img src="/pages/dashboard/astronaut-need-help.gif" alt="FAQ Link" className="w-36 h-36 object-contain" />
                  {/* <Tooltip id="launch-sneed-help-tooltip" isOpen={true} className="!bg-white !text-black !font-bold !rounded-tl-xl !rounded-tr-xl !rounded-br-xl xl:-ml-10 lg:-ml-2 ml-20" classNameArrow="!-left-3.5 !rounded-br-lg cut-corner-bl !overflow-hidden " arrowSize={30} /> */}
                </a>
                <LaunchSection 
                  enrolledCount={userData?.enrolledPathways || 0}
                  maxSlots={userData?.maxPathwaySlots || userData?.enrolledPathways || 0}
                  loading={loading}
                  hasSubscription={hasSubscription}
                  onLaunch={async () => {
                    // Check subscription status before showing onboarding modal
                    try {
                      const profileResponse = await dashboardService.getUserProfile();
                      const profileData = (profileResponse?.data as any)?.data as any;
                      const subscription = profileData?.subscription || profileData?.data?.subscription;
                      const hasActiveSubscription = subscription !== null && subscription !== undefined;
                      
                      // Only show onboarding modal if user has subscription
                      if (hasActiveSubscription) {
                        setShowOnboarding(true);
                      } else {
                        // Optionally navigate to subscription page or show message
                        navigate('/profile?tab=subscription');
                      }
                    } catch (error) {
                      console.error('Failed to check subscription status:', error);
                      // Don't show modal if we can't verify subscription
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>        

        {/* Onboarding Modal */}
        <OnboardingModal
          open={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          onSkip={handleOnboardingSkip}
          onContinue={handleOnboardingContinue}
        />
      </div>
      

      <div className="w-full bg-black">
        <div className="max-w-7xl mx-auto pt-10">
          <OnlineCoaching
            userData={userData as any} 
            events={coachingData}
            loading={coaLoading}
            error={coaError}
          />
        </div>
        <div className="max-w-7xl mx-auto pt-10">
          <ProfessionalNetworking
            userData={userData as any} 
            events={networkingData}
            loading={netLoading}
            error={netError}
          />
        </div>
      </div>
    </>
  );
};

export default Dashboard;